import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { contactPage, siteConfig } from '../../data/mock';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Button } from '../../components/ui/button';
import AnimateOnScroll from '../../components/AnimateOnScroll';
import Footer from '../../components/layout/Footer';

const Contact = () => {
  const { language } = useLanguage();
  const content = contactPage[language];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    privacy: false,
  });

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    setSubmitted(true);
    setTimeout(() => {
      setFormData({ name: '', email: '', subject: '', message: '', privacy: false });
      setSubmitted(false);
    }, 3000);
  };

  return (
    <div className="min-h-screen relative">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80')`,
        }}
      >
        <div className="absolute inset-0 bg-[#1a2a3a]/90" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col px-4 sm:px-6 pt-24 sm:pt-32 pb-20 sm:pb-24">
        <AnimateOnScroll animation="fadeInUp">
          <h1 className="text-white text-3xl sm:text-4xl md:text-5xl font-light text-center mb-10 sm:mb-16">
            {content.title}
          </h1>
        </AnimateOnScroll>

        <div className="max-w-6xl w-full mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 sm:gap-16">
          {/* Information */}
          <AnimateOnScroll animation="fadeInLeft" delay={200}>
            <div>
              <h2 className="text-white text-xl sm:text-2xl mb-6 sm:mb-8">{content.infoTitle}</h2>
              
              <div className="space-y-6 sm:space-y-8">
                <div className="flex items-start gap-3 sm:gap-4">
                  <Mail className="text-[#3eb8b3] shrink-0" size={22} />
                  <div>
                    <p className="text-[#3eb8b3] text-xs sm:text-sm tracking-widest mb-1">{content.labels.email}</p>
                    <a href={`mailto:${siteConfig.email}`} className="text-white hover:text-[#3eb8b3] transition-colors text-base sm:text-lg break-all">
                      {siteConfig.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <Phone className="text-[#3eb8b3] shrink-0" size={22} />
                  <div>
                    <p className="text-[#3eb8b3] text-xs sm:text-sm tracking-widest mb-1">{content.labels.phone}</p>
                    <a href={`tel:${siteConfig.phone}`} className="text-white hover:text-[#3eb8b3] transition-colors text-base sm:text-lg">
                      {siteConfig.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <MapPin className="text-[#3eb8b3] shrink-0" size={22} />
                  <div>
                    <p className="text-[#3eb8b3] text-xs sm:text-sm tracking-widest mb-1">{content.labels.location}</p>
                    <p className="text-white text-base sm:text-lg">{siteConfig.address}</p>
                    <p className="text-white text-base sm:text-lg">{siteConfig.city}</p>
                  </div>
                </div>
              </div>
            </div>
          </AnimateOnScroll>

          {/* Contact Form */}
          <AnimateOnScroll animation="fadeInRight" delay={400}>
            <div>
              <h2 className="text-white text-xl sm:text-2xl mb-6 sm:mb-8">{content.formTitle}</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <Input
                  placeholder={`${content.labels.name} *`}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#3eb8b3]"
                  required
                />
                
                <Input
                  type="email"
                  placeholder={`${content.labels.emailField} *`}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#3eb8b3]"
                  required
                />
                
                <Input
                  placeholder={content.labels.subject}
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#3eb8b3]"
                />
                
                <Textarea
                  placeholder={`${content.labels.message} *`}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#3eb8b3] min-h-[120px] sm:min-h-[150px]"
                  required
                />

                <p className="text-white/60 text-xs leading-relaxed">
                  {language === 'es'
                    ? <>Los datos facilitados serán tratados por INVERSIÓN EN ACTIVOS BANCARIOS, PK, S.L. ("OKUANT") para contactar y atender su petición, legitimado en el consentimiento mostrado al remitir el presente formulario. Puede obtener más información acerca del tratamiento de sus datos en nuestra <a href="/politica-privacidad" className="text-[#3eb8b3] hover:underline">Política de Privacidad</a> y ejercitar sus derechos de protección de datos a través de <a href="mailto:privacidad@okuant.com" className="text-[#3eb8b3] hover:underline">privacidad@okuant.com</a></>
                    : <>The data provided will be processed by INVERSIÓN EN ACTIVOS BANCARIOS, PK, S.L. ("OKUANT") to contact you and attend your request, legitimized by the consent shown when submitting this form. You can obtain more information about the processing of your data in our <a href="/politica-privacidad" className="text-[#3eb8b3] hover:underline">Privacy Policy</a> and exercise your data protection rights through <a href="mailto:privacidad@okuant.com" className="text-[#3eb8b3] hover:underline">privacidad@okuant.com</a></>
                  }
                </p>

                <p className="text-white/50 text-xs">
                  {language === 'es'
                    ? '(*) Los datos indicados con un asterisco son obligatorios para poder gestionar su petición.'
                    : '(*) Fields marked with an asterisk are required to process your request.'
                  }
                </p>
                
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy"
                    checked={formData.privacy}
                    onCheckedChange={(checked) => setFormData({ ...formData, privacy: checked })}
                    className="border-white/30 data-[state=checked]:bg-[#3eb8b3] data-[state=checked]:border-[#3eb8b3] mt-0.5"
                  />
                  <label htmlFor="privacy" className="text-white/80 text-xs sm:text-sm cursor-pointer leading-snug">
                    {content.labels.privacy}
                  </label>
                </div>
                
                <Button
                  type="submit"
                  disabled={!formData.privacy || submitted}
                  className="bg-[#3eb8b3] hover:bg-[#2ea09b] text-white px-8 py-3 w-full sm:w-auto disabled:opacity-50 hover-glow"
                >
                  {submitted ? (
                    <span>{language === 'es' ? 'Enviado!' : 'Sent!'}</span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      {content.labels.submit}
                      <Send size={16} />
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </AnimateOnScroll>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default Contact;
